import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Descriptions, Tag, Button, Alert, Space, Typography, Spin, theme } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import apiClient from '../api/client';

const { Text, Paragraph } = Typography;

// Everything known about a worker, on one screen, for diagnosing it.
//
// Two different sources, and they must not be confused: the panels above are
// what the worker said about itself when it connected — the gateway has held
// that since the handshake and keeps it after the link drops — while "Probe"
// goes to the host over HTTP right now. The second one still works when the
// WebSocket does not, which is exactly when it is wanted.

export interface WorkerInfoTarget {
  id: string;
  name: string;
  url: string;
  online: boolean;
  runtime?: Record<string, any>;
  software?: { state?: string; runningSha256?: string; installedSha256?: string; artifactSha256?: string };
  install?: { status?: string; at?: string; sshUser?: string; sshPort?: number; hostKeyFingerprint?: string;
              binarySha256?: string; failedStage?: string; cause?: string };
  geo?: { publicIp?: string; country?: string; city?: string; isp?: string };
  targets?: WorkerTargetState[];
}

// One check target as this worker sees it when it asks the target directly,
// with no proxy in the way.
export interface WorkerTargetState {
  url: string;
  alive: boolean;
  dead?: boolean;
  failedProbes?: number;
  latencyMs?: number;
  error?: string;
  contentChecked?: boolean;
  checkedAt?: string;
  deadSince?: string;
  lastAliveAt?: string;
}

interface Probe {
  url: string;
  reachable: boolean;
  error?: string;
  httpStatus?: number;
  latencyMs?: number;
  healthz?: Record<string, any>;
  raw?: string;
  tokenAccepted?: boolean;
}

const SOFTWARE: Record<string, { text: string; color: string }> = {
  current: { text: 'the same build this panel installs', color: 'green' },
  outdated: { text: 'a different build from the one this panel installs', color: 'orange' },
  present: { text: 'a checker is running; this panel has nothing to compare it with', color: 'default' },
  absent: { text: 'the panel removed the software from this host', color: 'default' },
  unknown: { text: 'the worker is not connected — nobody can say', color: 'default' },
  builtin: { text: 'built in — runs beside the panel, not installed over SSH', color: 'blue' },
};

const sha = (s?: string) => (s ? `${s.slice(0, 12)}…` : '—');

// One line per fact: the label and content cells are held at 12px with tight
// padding so a modal of ten rows is a screenful rather than two.
const DENSE_LABEL = { fontSize: 12, padding: '4px 10px', width: 132 };
const DENSE_CONTENT = { fontSize: 12, padding: '4px 10px' };

const WorkerInfoModal: React.FC<{ target: WorkerInfoTarget | null; onClose: () => void }> = ({ target, onClose }) => {
  const { token } = theme.useToken();
  const [probe, setProbe] = useState<Probe | null>(null);
  const [probing, setProbing] = useState(false);

  const runProbe = useCallback(async () => {
    if (!target) return;
    setProbing(true);
    try {
      const { data } = await apiClient.get(`/admin/workers/${target.id}/healthz`);
      setProbe(data.data);
    } catch (e: any) {
      setProbe({ url: target.url, reachable: false, error: e.response?.data?.error || 'The request failed' });
    } finally {
      setProbing(false);
    }
  }, [target]);

  useEffect(() => {
    if (target) runProbe();
  }, [target, runProbe]);

  const rt = target?.runtime;
  const sw = target?.software;
  const verdict = sw?.state ? SOFTWARE[sw.state] || { text: sw.state, color: 'default' } : null;

  return (
    <Modal
      open={!!target}
      title={`Worker ${target?.name || ''}`}
      onCancel={onClose}
      afterClose={() => { setProbe(null); setProbing(false); }}
      width={760}
      footer={<Button onClick={onClose}>Close</Button>}
      destroyOnClose
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Descriptions size="small" column={2} bordered
          labelStyle={DENSE_LABEL} contentStyle={DENSE_CONTENT}
          title={<Space size={6}><Text strong style={{ fontSize: 13 }}>What the worker reported about itself</Text>{target?.online
            ? <Tag color="success" style={{ marginInlineEnd: 0 }}>connected</Tag>
            : <Tag color="error" style={{ marginInlineEnd: 0 }}>not connected</Tag>}</Space>}>
          <Descriptions.Item label="Address">{target?.url}</Descriptions.Item>
          <Descriptions.Item label="Listening on">{rt?.listen || '—'}</Descriptions.Item>
          <Descriptions.Item label="Hostname">{rt?.hostname || '—'}</Descriptions.Item>
          <Descriptions.Item label="Supervised by">{rt?.launcher || '—'}{rt?.pid ? ` (pid ${rt.pid})` : ''}</Descriptions.Item>
          <Descriptions.Item label="Version">{rt?.version || '—'}{rt?.commit ? ` · ${String(rt.commit).slice(0, 12)}` : ''}</Descriptions.Item>
          <Descriptions.Item label="System">{rt ? `${rt.os}/${rt.arch}, ${rt.cpus} CPUs` : '—'}</Descriptions.Item>
          <Descriptions.Item label="Local addresses">{rt?.localIps?.join(', ') || '—'}</Descriptions.Item>
          <Descriptions.Item label="Public address">
            {target?.geo?.publicIp || '—'}
            {target?.geo?.city ? ` · ${target.geo.city}, ${target.geo.country}` : ''}
          </Descriptions.Item>
          <Descriptions.Item label="Started at">
            {rt?.startedAt ? new Date(rt.startedAt).toLocaleString() : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Concurrent checks">{rt?.maxConcurrency ?? '—'}</Descriptions.Item>
        </Descriptions>

        <Descriptions size="small" column={1} bordered
          labelStyle={DENSE_LABEL} contentStyle={DENSE_CONTENT}
          title={<Text strong style={{ fontSize: 13 }}>Software on the host</Text>}>
          <Descriptions.Item label="Verdict">
            {verdict ? <Tag color={verdict.color}>{verdict.text}</Tag> : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Running now">
            <Text code>{sha(sw?.runningSha256)}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="This panel installs">
            <Text code>{sha(sw?.artifactSha256)}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="This panel put there">
            {target?.install
              ? <Space size={8}>
                  <Text code>{sha(sw?.installedSha256)}</Text>
                  <Text type="secondary">
                    {target.install.status}
                    {target.install.at ? `, ${new Date(target.install.at).toLocaleString()}` : ''}
                    {target.install.sshUser ? `, over SSH as ${target.install.sshUser}` : ''}
                  </Text>
                </Space>
              : <Text type="secondary">this panel never installed anything here — the host was set up by hand</Text>}
          </Descriptions.Item>
          {target?.install?.hostKeyFingerprint && (
            <Descriptions.Item label="Host key">
              <Text code style={{ fontSize: 12 }}>{target.install.hostKeyFingerprint}</Text>
            </Descriptions.Item>
          )}
        </Descriptions>

        <div>
          <Space style={{ marginBottom: 6 }} align="baseline">
            <Text strong style={{ fontSize: 13 }}>Check targets, as this worker sees them</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>asked directly, no proxy in between</Text>
          </Space>
          {!target?.targets?.length ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Nothing measured yet. A worker probes the targets when it starts and every 30 minutes
              after that — an older build never does, and reports nothing here.
            </Text>
          ) : (
            <Descriptions size="small" column={1} bordered
              labelStyle={{ ...DENSE_LABEL, width: 240 }} contentStyle={DENSE_CONTENT}>
              {target.targets.map((t) => (
                <Descriptions.Item
                  key={t.url}
                  label={<Text style={{ fontSize: 12 }}>{t.url}</Text>}
                >
                  <Space size={8} wrap>
                    {t.dead
                      ? <Tag color="error">not reachable from here — still used in checks</Tag>
                      : t.alive
                        ? <Tag color="success">answers{t.contentChecked ? ' with the expected content' : ''}</Tag>
                        : <Tag color="warning">failing, still in use</Tag>}
                    {t.alive && t.latencyMs ? <Text type="secondary" style={{ fontSize: 12 }}>{Math.round(t.latencyMs)} ms</Text> : null}
                    {t.error ? <Text type="secondary" style={{ fontSize: 12 }}>{t.error}</Text> : null}
                    {t.checkedAt ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        checked {new Date(t.checkedAt).toLocaleString()}
                      </Text>
                    ) : null}
                  </Space>
                </Descriptions.Item>
              ))}
            </Descriptions>
          )}
        </div>

        <div>
          <Space style={{ marginBottom: 6 }}>
            <Text strong style={{ fontSize: 13 }}>Probe the host now</Text>
            <Button size="small" icon={<ReloadOutlined />} loading={probing} onClick={runProbe}>
              Probe
            </Button>
          </Space>
          {probing && !probe && <div style={{ padding: 12 }}><Spin size="small" /></div>}
          {probe && !probe.reachable && (
            <Alert type="error" showIcon
              message={`No answer from ${probe.url}`}
              description={<>
                <Paragraph style={{ marginBottom: 4 }}>{probe.error}</Paragraph>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  This is the same request the panel makes after an install. The usual reasons are a
                  service that is not running, or a firewall in front of the port.
                </Text>
              </>} />
          )}
          {probe && probe.reachable && probe.tokenAccepted === false && (
            <Alert type="warning" showIcon style={{ marginBottom: 8 }}
              message="This worker answers, but not to us"
              description="It is running and reachable, and it rejected this panel's token — so it keeps the detailed answer to itself and will not accept jobs. Press Install to issue it a new one." />
          )}
          {probe && probe.reachable && (
            <>
              <Space size={8} style={{ marginBottom: 8 }}>
                <Tag color={probe.httpStatus === 200 ? 'success' : 'error'}>HTTP {probe.httpStatus}</Tag>
                <Text type="secondary" style={{ fontSize: 12 }}>{probe.latencyMs} ms · {probe.url}</Text>
              </Space>
              <pre style={{
                maxHeight: 220, overflow: 'auto', fontSize: 12, margin: 0,
                background: token.colorFillQuaternary,
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadius, padding: 8,
              }}>
                {JSON.stringify(probe.healthz ?? probe.raw, null, 2)}
              </pre>
            </>
          )}
        </div>
      </Space>
    </Modal>
  );
};

export default WorkerInfoModal;
